import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type MiniProfile = {
  user_id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
};

// Fetches profile rows for an arbitrary set of user_ids. Cached per-set so
// re-renders don't re-fetch the same batch.
const profileCache = new Map<string, MiniProfile>();

export function useProfilesByIds(userIds: string[]) {
  const [profiles, setProfiles] = useState<Map<string, MiniProfile>>(() => {
    const m = new Map<string, MiniProfile>();
    for (const id of userIds) {
      const cached = profileCache.get(id);
      if (cached) m.set(id, cached);
    }
    return m;
  });

  useEffect(() => {
    const missing = userIds.filter((id) => !profileCache.has(id));
    if (missing.length === 0) {
      // ensure state is in sync with cache
      setProfiles(() => {
        const m = new Map<string, MiniProfile>();
        for (const id of userIds) {
          const c = profileCache.get(id);
          if (c) m.set(id, c);
        }
        return m;
      });
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        // public_profiles view: excludes phone / phone_verified_at and
        // hides profiles with is_public = false. Generic display lookups
        // (avatar/name) must go through this view, not the base table.
        .from("public_profiles")
        .select("user_id, username, display_name, avatar_url")
        .in("user_id", missing);
      if (cancelled) return;
      for (const p of data ?? []) profileCache.set(p.user_id, p as MiniProfile);
      setProfiles(() => {
        const m = new Map<string, MiniProfile>();
        for (const id of userIds) {
          const c = profileCache.get(id);
          if (c) m.set(id, c);
        }
        return m;
      });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIds.join(",")]);

  return profiles;
}
